import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAccess } from "./AccessContext";

const WorkspaceContext = createContext(null);
const WORKSPACE_STORAGE_KEY = "workspaceDepartmentCode";
// Temporary QA lockdown. Remove this set to show every department again.
const VISIBLE_DEPARTMENTS = new Set(["OPERATIONS", "UNDERWRITING", "CLAIMS"]);

function shownWorkspaces(workspaces) {
  if (!Array.isArray(workspaces)) return workspaces;
  return workspaces.filter((workspace) => VISIBLE_DEPARTMENTS.has(workspace.code));
}

export function shownDepartments(departments) {
  if (!Array.isArray(departments)) return [];
  return departments.filter((team) => VISIBLE_DEPARTMENTS.has(String(team?.code || "").toUpperCase()));
}

function readStoredRole() {
  try {
    return sessionStorage.getItem(WORKSPACE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function writeStoredRole(code) {
  try {
    if (code) sessionStorage.setItem(WORKSPACE_STORAGE_KEY, code);
  } catch {
    // Storage can be blocked. The open workspace still stays in memory.
  }
}

export function queueDestinationOptions(departments, exclude = []) {
  const skip = new Set(exclude.map((code) => String(code || "").toUpperCase()));
  const options = [];
  const seen = new Set();
  (Array.isArray(departments) ? departments : []).forEach((team) => {
    const value = team?.queue_code;
    if (!value || seen.has(value)) return;
    if (skip.has(value) || skip.has(String(team.code || "").toUpperCase())) return;
    seen.add(value);
    options.push({ value, label: team.label || value });
  });
  return options;
}

export function workspaceMeta(workspace) {
  const name = String(workspace?.label || "").trim();
  return {
    name,
    detail: workspace?.queue_label || "",
  };
}

function preferredWorkspace(workspaces, current) {
  const chosen = current || readStoredRole();
  if (chosen && workspaces.some((workspace) => workspace.code === chosen)) return chosen;
  return workspaces[0]?.code || "";
}

export function WorkspaceProvider({ children }) {
  const { workspaces: assignedWorkspaces, loading } = useAccess();
  const workspaces = useMemo(
    () => shownWorkspaces(assignedWorkspaces),
    [assignedWorkspaces],
  );
  const [roleCode, setRoleCodeState] = useState("");

  useEffect(() => {
    if (loading) return;
    setRoleCodeState((current) => {
      const next = preferredWorkspace(workspaces || [], current);
      writeStoredRole(next);
      return next;
    });
  }, [loading, workspaces]);

  const setTeamCode = useCallback((code) => {
    writeStoredRole(code);
    setRoleCodeState(code);
  }, []);

  const teams = Array.isArray(workspaces) ? workspaces : null;
  const value = useMemo(() => ({
    teams,
    teamCode: roleCode,
    setTeamCode,
    ready: !loading,
    activeTeam: teams?.find((workspace) => workspace.code === roleCode) || teams?.[0] || null,
  }), [teams, roleCode, setTeamCode, loading]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  return useContext(WorkspaceContext) || {
    teams: null,
    teamCode: "",
    setTeamCode: () => {},
    activeTeam: null,
    ready: true,
  };
}
